/**
 * TabLayout.jsx
 *
 * Layout wrapper for property tabs that provides consistent
 * main content + context sidebar structure.
 *
 * Usage:
 * <TabLayout sidebar={<MySidebarContent />}>
 *   <MyMainContent />
 * </TabLayout>
 */

import React from "react";
import ContextSidebar from "./ContextSidebar.jsx";

export default function TabLayout({
  children,
  sidebar,
  sidebarTitle = "Details",
  sidebarWidth = "w-80",
  sidebarPosition = "right",
  sidebarDefaultCollapsed = false,
  storageKey,
  className = "",
}) {
  // Generate storage key based on sidebar title if not provided
  const resolvedStorageKey = storageKey || `sidebar-${sidebarTitle.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className={`flex min-h-[500px] ${className}`}>
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        {children}
      </div>

      {/* Context Sidebar */}
      {sidebar && (
        <ContextSidebar
          title={sidebarTitle}
          width={sidebarWidth}
          position={sidebarPosition}
          defaultCollapsed={sidebarDefaultCollapsed}
          storageKey={resolvedStorageKey}
        >
          {sidebar}
        </ContextSidebar>
      )}
    </div>
  );
}

/**
 * TabLayoutMain - Semantic wrapper for main content
 */
export function TabLayoutMain({ children, className = "" }) {
  return (
    <div className={`space-y-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * TabLayoutCard - Styled card for tab content sections
 */
export function TabLayoutCard({ title, icon: Icon, children, className = "", actions }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5 text-hn-red" />}
            {title && (
              <h3 className="text-lg font-semibold text-hn-dark">{title}</h3>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}

/**
 * TabLayoutGrid - Grid layout for cards
 */
export function TabLayoutGrid({ children, cols = 3, className = "" }) {
  const colsClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  }[cols] || "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  return (
    <div className={`grid ${colsClass} gap-4 ${className}`}>
      {children}
    </div>
  );
}
